import React, { useState, useEffect } from 'react';
import { responseHero } from '../../../../utils/axiosInterceptor';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../store/reducers/userSlice';
import { getBMPDepositGroup } from '../../../../api/getBMPDepositGroup';
import { BMP_DepositGroup } from '../../../../models/bmp.model';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const AddNewRobotProductionModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
    const user = useSelector(selectUser);
    
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        assetType: 1, // Alapértelmezett pl. BTC
        producedQuantity: '',
        note: '',
    });

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const userId = user?.userInfo?.user_id;
        if (!userId) {
            alert("Hiba: Nincs bejelentkezett felhasználó!");
            return;
        }

        try {
            // A backend 'robotProductionData' kulcsot vár a body-ban
            const payload = {
                robotProductionData: {
                    date: new Date(formData.date).toISOString(),
                    assetType: Number(formData.assetType),
                    producedQuantity: Number(formData.producedQuantity) || 0,
                    note: formData.note,
                }
            };

            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/robot`, payload);
            
            // Sikeres mentés után alaphelyzet
            setFormData({
                date: new Date().toISOString().split('T')[0],
                assetType: 1,
                producedQuantity: '',
                note: '',
            });
            
            onSuccess();
        } catch (err: any) {
            console.error("Backend hiba:", err.response?.data);
            alert(`Hiba a mentés során: ${err.response?.data?.message || "Ismeretlen hiba"}`);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-blue-50/30">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 tracking-tight">Robot Termelés</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Napi hozam rögzítése</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl transition-colors">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-5">
                    {/* Dátum */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-blue-600 mb-1">Termelés Napja</label>
                        <input 
                            type="date" 
                            required 
                            className="w-full bg-blue-50/30 border border-blue-100 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 ring-blue-500" 
                            value={formData.date} 
                            onChange={(e) => setFormData({...formData, date: e.target.value})} 
                        />
                    </div>

                    {/* Asset Választó */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Termelt Eszköz</label>
                        <select 
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 outline-none focus:ring-2 ring-blue-500" 
                            value={formData.assetType} 
                            onChange={(e) => setFormData({...formData, assetType: Number(e.target.value)})}
                        >
                            <option value={1}>Bitcoin (BTC)</option>
                        </select>
                    </div>

                    {/* Termelt Mennyiség */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Mennyiség (Produced Quantity)</label>
                        <div className="relative">
                            <input 
                                type="number" 
                                step="any" 
                                required 
                                placeholder="0.00000000"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 ring-blue-500" 
                                value={formData.producedQuantity} 
                                onChange={(e) => setFormData({...formData, producedQuantity: e.target.value})} 
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400">UNIT</span>
                        </div>
                    </div>

                    {/* Megjegyzés */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Megjegyzés</label>
                        <textarea 
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-blue-500"
                            rows={3}
                            placeholder="Opcionális megjegyzés a termeléshez..."
                            value={formData.note}
                            onChange={(e) => setFormData({...formData, note: e.target.value})}
                        />
                    </div>

                    {/* Submit Gomb */}
                    <button 
                        type="submit"
                        disabled={!formData.producedQuantity}
                        className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-white transition-all shadow-lg active:scale-95 ${
                            formData.producedQuantity ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'
                        }`}
                    >
                        Termelés Mentése
                    </button>
                </form>
            </div>
        </div>
    );
};